import { useState, useMemo, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, ArrowLeft, Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import TechBackground from "@/components/TechBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Consume a password-reset token (arrives via /reset-password?token=…).
 *
 * On success the backend signs the user in via the tm_auth httpOnly cookie.
 * Phase B (cookie-only): no client-side token caching — we just refresh the
 * auth context so AuthContext picks up the new /auth/me result.
 */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { refresh } = useAuth();
    const token = params.get("token") || "";

    const [pw1, setPw1] = useState("");
    const [pw2, setPw2] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    // Friendly bounce — if the URL is missing a token, nudge back to forgot.
    useEffect(() => {
        if (!token) {
            toast.error("Reset link is missing the token. Request a fresh email.");
        }
    }, [token]);

    // Lightweight strength meter — 0..4. Pure heuristic, never blocks submit.
    const strength = useMemo(() => {
        let s = 0;
        if (pw1.length >= 8) s++;
        if (/[A-Z]/.test(pw1) && /[a-z]/.test(pw1)) s++;
        if (/\d/.test(pw1)) s++;
        if (/[^\w\s]/.test(pw1) || pw1.length >= 14) s++;
        return s;
    }, [pw1]);

    const tooShort = pw1.length > 0 && pw1.length < 8;
    const mismatch = pw1 && pw2 && pw1 !== pw2;
    const canSubmit = token && pw1 && pw2 && !tooShort && !mismatch && !busy;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/auth/reset-password`, {
                token, new_password: pw1,
            });
            setDone(true);
            // Backend sets the httpOnly cookie on success — sync AuthContext
            // to pick up the new session.
            await refresh();
            toast.success("Password updated. Signing you in…");
            const dest = data?.user?.role && data.user.role !== "client" ? "/admin" : "/portal";
            setTimeout(() => nav(dest, { replace: true }), 800);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not reset password. Try requesting a new link.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="reset-password-page" className="min-h-[calc(100vh-68px)] grid lg:grid-cols-2 bg-white">
            {/* Visual side */}
            <div className="relative hidden lg:block bg-[#0B1120] tr-grain overflow-hidden">
                <TechBackground variant="dark" density={1.0} />
                <div className="absolute -top-32 -right-20 w-[500px] h-[500px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[420px] h-[420px] tr-glow bg-teal-500/25" />
                <div className="relative h-full flex flex-col justify-between p-12 text-white">
                    <Link to="/" className="font-display text-xl tracking-tight">
                        Toothrocket<span className="text-[10px] align-top ml-0.5 text-white/40">™</span>
                    </Link>
                    <div>
                        <div className="tr-overline text-cyan-400 mb-3">· Pick a new password</div>
                        <h1 className="font-display font-light text-5xl tracking-tight leading-[1.05]">
                            Choose something<br />
                            <span className="italic text-cyan-300/90 font-extralight">strong & memorable.</span>
                        </h1>
                        <p className="mt-6 text-white/70 max-w-md text-[15px] leading-relaxed">
                            Once you set it, we'll burn the link and sign you in. Every other open session for this account is invalidated.
                        </p>
                    </div>
                    <div className="font-mono-tr text-[11px] tracking-[0.2em] uppercase text-white/40 inline-flex items-center gap-2">
                        <ShieldCheck size={14} /> Bcrypt · Single-use link
                    </div>
                </div>
            </div>

            {/* Form side */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <Link to="/login" data-testid="reset-back-to-login" className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 mb-6">
                        <ArrowLeft size={14} /> Back to sign in
                    </Link>

                    {!token ? (
                        <div data-testid="reset-no-token" className="text-center lg:text-left">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-rose-50 text-rose-600 mb-5">
                                <AlertTriangle size={26} />
                            </div>
                            <h1 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">Missing reset token.</h1>
                            <p className="mt-3 text-slate-600 text-[14.5px]">This page expects a one-time token from a password-reset email. Request a fresh link below.</p>
                            <Link
                                to="/forgot-password"
                                data-testid="reset-request-new"
                                className="mt-6 inline-flex items-center gap-2 tr-btn !py-3 !px-5"
                            >
                                Request a new link <ArrowRight size={14} />
                            </Link>
                        </div>
                    ) : done ? (
                        <div data-testid="reset-success">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-5">
                                <ShieldCheck size={26} />
                            </div>
                            <h1 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">All set.</h1>
                            <p className="mt-3 text-slate-600 text-[14.5px]">Your password is updated. Redirecting you to the portal…</p>
                        </div>
                    ) : (
                        <>
                            <div className="tr-overline mb-3">· New password</div>
                            <h1 className="font-display font-light text-4xl tracking-tight leading-tight text-slate-900">Pick a new password.</h1>
                            <p className="mt-2 text-slate-600 text-[14.5px]">At least 8 characters. Mix letters, numbers, and a symbol for best results.</p>

                            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-form">
                                <label className="block">
                                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-2">New password</div>
                                    <div className="relative">
                                        <input
                                            data-testid="reset-pw1"
                                            type={show ? "text" : "password"}
                                            required
                                            autoFocus
                                            value={pw1}
                                            onChange={(e) => setPw1(e.target.value)}
                                            className="w-full px-4 py-3 pr-12 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition outline-none text-[14.5px]"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShow((v) => !v)}
                                            data-testid="reset-pw-toggle"
                                            aria-label={show ? "Hide password" : "Show password"}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                                        >
                                            {show ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                    {/* Strength meter */}
                                    <div className="flex gap-1 mt-2" aria-hidden="true">
                                        {[1, 2, 3, 4].map((i) => {
                                            const active = strength >= i;
                                            const cls = !active
                                                ? "bg-slate-200"
                                                : strength <= 1 ? "bg-rose-400"
                                                : strength === 2 ? "bg-amber-400"
                                                : strength === 3 ? "bg-cyan-500" : "bg-emerald-500";
                                            return <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${cls}`} />;
                                        })}
                                    </div>
                                    {tooShort && (
                                        <p data-testid="reset-too-short" className="mt-2 text-[12px] text-rose-600">Use at least 8 characters.</p>
                                    )}
                                </label>

                                <label className="block">
                                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-2">Confirm new password</div>
                                    <input
                                        data-testid="reset-pw2"
                                        type={show ? "text" : "password"}
                                        required
                                        value={pw2}
                                        onChange={(e) => setPw2(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition outline-none text-[14.5px]"
                                        placeholder="••••••••"
                                    />
                                    {mismatch && (
                                        <p data-testid="reset-mismatch" className="mt-2 text-[12px] text-rose-600">Passwords don't match.</p>
                                    )}
                                </label>

                                <button
                                    data-testid="reset-submit"
                                    type="submit"
                                    disabled={!canSubmit}
                                    className="w-full tr-btn justify-center !py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {busy
                                        ? (<><Loader2 size={16} className="animate-spin" /> Updating…</>)
                                        : (<>Update password <ArrowRight size={16} /></>)}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
